// src/components/mobile-onboarding-cta.tsx
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export const MobileOnboardingCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const businessId = localStorage.getItem('businessId') || 'default';
  const storageKey = `staffluent_mobile_onboarding_dismissed_${businessId}`;
  const onboardingStorageKey = `staffluent_setup_progress_${businessId}`;

  // Required steps for completion
  const requiredSteps = ["welcome", "departments", "roles", "employees", "teams"];

  const checkOnboardingCompletion = () => {
    try {
      const onboardingData = localStorage.getItem(onboardingStorageKey);
      if (!onboardingData) return false;

      const parsed = JSON.parse(onboardingData);
      const completedSteps = parsed.completedSteps || [];

      // Check if all required steps are completed
      return requiredSteps.every(step => completedSteps.includes(step));
    } catch (error) {
      console.error('Error checking onboarding completion:', error);
      return false;
    }
  };

  useEffect(() => {
    // Check if mobile or PWA
    const isMobile = window.innerWidth < 768;
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  (window.navigator as any).standalone === true;
    
    // Only show for mobile/PWA users
    if (!isMobile && !isPWA) {
      setIsVisible(false);
      return;
    }

    // Check if user has manually dismissed this before
    const isDismissed = localStorage.getItem(storageKey);
    if (isDismissed) {
      setIsVisible(false);
      return;
    }

    // Check if onboarding is already completed
    const isOnboardingCompleted = checkOnboardingCompletion();
    if (isOnboardingCompleted) {
      setIsVisible(false);
      return;
    }

    // Show the CTA if none of the above conditions are met
    setIsVisible(true);
  }, [storageKey, onboardingStorageKey]);

  const handleStartOnboarding = () => {
    // Mark as dismissed so it won't show again
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
    
    // Navigate to onboarding page
    navigate('/business-onboarding');
  };

  const handleDismiss = () => {
    // Mark as dismissed so it won't show again
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <Card className="border-blue-200 mt-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 mx-4 mb-3">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 bg-blue-100 rounded-lg dark:bg-blue-900/50 flex-shrink-0">
              <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 text-sm">
                  Complete Business Setup
                </h3>
                <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                  5 steps
                </Badge>
              </div>
              <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                Set up your business structure to unlock all Staffluent features on mobile.
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="text-blue-600 hover:text-blue-700 h-6 w-6 p-0 flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="mt-3 flex gap-2">
          <Button
            onClick={handleStartOnboarding}
            className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
            size="sm"
          >
            Get Started
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};